import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { loadOwnBusiness, type BusinessStatus } from '../lib/business/own-business';
import { useAuthContext } from './auth-context';

export type ShellContexto = 'consumer' | 'professional';

export interface ShellContextValue {
  contexto: ShellContexto;
  podeAlternar: boolean;
  estadoNegocio: BusinessStatus | null;
  alternar: () => ShellContexto;
  anuncio: ShellContexto | null;
  isLoading: boolean;
  /**
   * T1.19 (/code-review): a leitura do negócio falhou. NÃO é "sem negócio" — tratá-lo assim fazia o
   * guard mandar um dono legítimo para `/home` numa rede instável.
   */
  falhaNegocio: boolean;
  /** Nova tentativa manual da leitura do negócio (o retry em `focus`/`online` continua armado). */
  recarregar: () => void;
}

const ShellContext = createContext<ShellContextValue | undefined>(undefined);

const STORAGE_PREFIX = 'powerful-shell-context:';
const ESTADOS_ELEGIVEIS = new Set<BusinessStatus>([
  'pending_payment',
  'pending_kyc',
  'active',
  'suspended',
]);

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function lerContextoPersistido(userId: string): ShellContexto {
  try {
    return localStorage.getItem(storageKey(userId)) === 'professional'
      ? 'professional'
      : 'consumer';
  } catch {
    return 'consumer';
  }
}

function persistirContexto(userId: string, contexto: ShellContexto): void {
  try {
    localStorage.setItem(storageKey(userId), contexto);
  } catch {
    // O contexto continua funcional em memória quando o armazenamento está indisponível.
  }
}

export function ShellContextProvider({ children }: { children: ReactNode }) {
  const { user, claims, isLoading: authLoading } = useAuthContext();
  const [contexto, setContexto] = useState<ShellContexto>('consumer');
  const [podeAlternar, setPodeAlternar] = useState(false);
  const [estadoNegocio, setEstadoNegocio] = useState<BusinessStatus | null>(null);
  const [anuncio, setAnuncio] = useState<ShellContexto | null>(null);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [retryRevision, setRetryRevision] = useState(0);
  const [falhaNegocio, setFalhaNegocio] = useState(false);
  const userId = user?.id ?? null;

  useEffect(() => {
    let ativo = true;
    let retryArmado = false;

    function removerRetryListeners() {
      window.removeEventListener('online', tentarNovamente);
      window.removeEventListener('focus', tentarNovamente);
    }

    function tentarNovamente() {
      if (!ativo || !retryArmado) return;

      retryArmado = false;
      removerRetryListeners();
      setRetryRevision((revision) => revision + 1);
    }

    setContexto('consumer');
    setPodeAlternar(false);
    setEstadoNegocio(null);
    setAnuncio(null);
    setFalhaNegocio(false);

    if (authLoading) {
      setBusinessLoading(true);
      return () => {
        ativo = false;
        removerRetryListeners();
      };
    }

    if (!userId || !claims.businessId) {
      setBusinessLoading(false);
      return () => {
        ativo = false;
        removerRetryListeners();
      };
    }

    setBusinessLoading(true);
    void loadOwnBusiness()
      .then((resultado) => {
        if (!ativo) return;

        // Falha fechada nesta montagem, mas não apaga uma escolha válida. Uma indisponibilidade
        // transitória não é evidência de que o negócio deixou de ser elegível.
        if (!resultado.ok) {
          setFalhaNegocio(true);
          retryArmado = true;
          window.addEventListener('online', tentarNovamente, { once: true });
          window.addEventListener('focus', tentarNovamente, { once: true });
          return;
        }

        const status = resultado.data?.status ?? null;
        const elegivel = status !== null && ESTADOS_ELEGIVEIS.has(status);

        setEstadoNegocio(status);
        setPodeAlternar(elegivel);
        const proximo = elegivel ? lerContextoPersistido(userId) : 'consumer';
        setContexto(proximo);
        if (!elegivel) persistirContexto(userId, 'consumer');
      })
      .finally(() => {
        if (ativo) setBusinessLoading(false);
      });

    return () => {
      ativo = false;
      removerRetryListeners();
    };
  }, [authLoading, claims.businessId, retryRevision, userId]);

  const alternar = useCallback((): ShellContexto => {
    if (!podeAlternar || !userId) return contexto;

    const proximo: ShellContexto = contexto === 'consumer' ? 'professional' : 'consumer';
    setContexto(proximo);
    setAnuncio(proximo);
    persistirContexto(userId, proximo);
    return proximo;
  }, [contexto, podeAlternar, userId]);

  const recarregar = useCallback(() => {
    setRetryRevision((revision) => revision + 1);
  }, []);

  const value = useMemo<ShellContextValue>(
    () => ({
      contexto,
      podeAlternar,
      estadoNegocio,
      alternar,
      anuncio,
      isLoading: authLoading || businessLoading,
      falhaNegocio,
      recarregar,
    }),
    [alternar, anuncio, authLoading, businessLoading, contexto, estadoNegocio, falhaNegocio, podeAlternar, recarregar],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShellContext(): ShellContextValue {
  const value = useContext(ShellContext);
  if (!value) throw new Error('useShellContext tem de ser usado dentro de <ShellContextProvider>.');
  return value;
}
